import { supabase } from '../lib/supabase';

/**
 * Service pour gérer les URLs bloquées (clients existants)
 * Empêche les clients actuels d'utiliser le système d'essai gratuit
 */
export const BlockedUrlService = {
  /**
   * Normalise une URL pour la comparaison
   * Enlève http://, https://, www., trailing slash, etc.
   */
  normalizeUrl(url: string): string {
    if (!url) return '';
    
    let normalized = url.toLowerCase().trim();
    
    // Enlever le protocole
    normalized = normalized.replace(/^https?:\/\//, '');
    
    // Enlever www.
    normalized = normalized.replace(/^www\./, '');
    
    // Enlever le trailing slash
    normalized = normalized.replace(/\/$/, '');
    
    return normalized;
  },

  /**
   * Extrait le domaine principal d'une URL
   * Exemple: "https://shop.swbo.ch/products" -> "shop.swbo.ch"
   */
  extractDomain(url: string): string {
    const normalized = this.normalizeUrl(url);
    
    // Enlever le path (tout après le premier /)
    const domain = normalized.split('/')[0];
    
    return domain;
  },

  /**
   * Vérifie si une URL est bloquée
   * Retourne true si l'URL ou son domaine est dans la liste de blocage
   */
  async isUrlBlocked(url: string): Promise<{
    isBlocked: boolean;
    reason?: string;
    blockedUrl?: string;
  }> {
    try {
      const normalizedUrl = this.normalizeUrl(url);
      const domain = this.extractDomain(url);

      console.log('🔍 [BlockedUrlService] Vérification de blocage:', {
        original: url,
        normalized: normalizedUrl,
        domain: domain
      });

      // Vérifier si l'URL exacte ou le domaine est bloqué
      const { data, error } = await supabase
        .from('blocked_urls')
        .select('url, domain, reason, notes')
        .eq('is_active', true)
        .or(`url.eq.${normalizedUrl},domain.eq.${domain}`)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('❌ [BlockedUrlService] Erreur lors de la vérification:', error);
        // En cas d'erreur, on ne bloque pas (fail-safe)
        return { isBlocked: false };
      }

      if (data) {
        console.log('🚫 [BlockedUrlService] URL bloquée détectée:', {
          blockedUrl: data.url,
          domain: data.domain,
          reason: data.reason,
          notes: data.notes
        });

        return {
          isBlocked: true,
          reason: data.reason || 'Client existant',
          blockedUrl: data.url
        };
      }

      console.log('✅ [BlockedUrlService] URL autorisée');
      return { isBlocked: false };

    } catch (error) {
      console.error('❌ [BlockedUrlService] Erreur inattendue:', error);
      // En cas d'erreur, on ne bloque pas (fail-safe)
      return { isBlocked: false };
    }
  },

  /**
   * Ajoute une URL à la liste de blocage
   */
  async blockUrl(
    url: string,
    reason: string = 'Client existant',
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const normalizedUrl = this.normalizeUrl(url);
      const domain = this.extractDomain(url);

      const { error } = await supabase
        .from('blocked_urls')
        .insert({
          url: normalizedUrl,
          domain: domain,
          reason: reason,
          notes: notes,
          is_active: true
        });

      if (error) {
        console.error('❌ [BlockedUrlService] Erreur lors du blocage:', error);
        return {
          success: false,
          message: `Erreur lors du blocage: ${error.message}`
        };
      }

      console.log('✅ [BlockedUrlService] URL bloquée avec succès:', normalizedUrl);
      return {
        success: true,
        message: 'URL bloquée avec succès'
      };

    } catch (error) {
      console.error('❌ [BlockedUrlService] Erreur inattendue:', error);
      return {
        success: false,
        message: 'Erreur inattendue lors du blocage'
      };
    }
  },

  /**
   * Retire une URL de la liste de blocage (désactivation)
   */
  async unblockUrl(url: string): Promise<{ success: boolean; message: string }> {
    try {
      const normalizedUrl = this.normalizeUrl(url);

      const { error } = await supabase
        .from('blocked_urls')
        .update({ is_active: false })
        .eq('url', normalizedUrl);

      if (error) {
        console.error('❌ [BlockedUrlService] Erreur lors du déblocage:', error);
        return {
          success: false,
          message: `Erreur lors du déblocage: ${error.message}`
        };
      }

      console.log('✅ [BlockedUrlService] URL débloquée avec succès:', normalizedUrl);
      return {
        success: true,
        message: 'URL débloquée avec succès'
      };

    } catch (error) {
      console.error('❌ [BlockedUrlService] Erreur inattendue:', error);
      return {
        success: false,
        message: 'Erreur inattendue lors du déblocage'
      };
    }
  },

  /**
   * Liste toutes les URLs bloquées actives
   */
  async listBlockedUrls(): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      url: string;
      domain: string;
      reason: string;
      notes?: string;
      blocked_at: string;
    }>;
    message?: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('blocked_urls')
        .select('id, url, domain, reason, notes, blocked_at')
        .eq('is_active', true)
        .order('blocked_at', { ascending: false });

      if (error) {
        console.error('❌ [BlockedUrlService] Erreur lors de la récupération:', error);
        return {
          success: false,
          message: `Erreur: ${error.message}`
        };
      }

      return {
        success: true,
        data: data || []
      };

    } catch (error) {
      console.error('❌ [BlockedUrlService] Erreur inattendue:', error);
      return {
        success: false,
        message: 'Erreur inattendue'
      };
    }
  }
};
